<template>
  <div>
    <v-autocomplete
      ref="autocomplete"
      v-model="internalValue"
      :label="label"
      :items="items"
      :loading="loading"
      :no-data-text="noDataText"
      :hide-no-data="hideNoData"
      :multiple="multiple"
      :clearable="clearable"
      :item-text="itemText"
      :item-value="itemValue"
      :placeholder="placeholder"
      :dense="dense"
      :outlined="outlined"
      :search-input.sync="search"
      :filter="filter"
      :disabled="disabled"
      @change="onChange"
      @update:search-input="onSearchInput"
      @blur="onBlur"
      @input="onSelect"
    />
  </div>
</template>

<script>
  export default {
    name: 'AutocompleteAssincrono',

    props: {
      value: {
        // <- v-model do pai
        type: [Array, Object, String, Number],
        default: () => []
      },
      service: { type: Object, required: true },
      serviceMethod: { type: String, default: 'search' },
      queryParamName: { type: String, default: 'q' },
      extraParams: { type: Object, default: () => ({}) },
      paramsTransform: { type: Function, default: null },
      responseParser: {
        type: Function,
        default: (res) => (res && res.data !== undefined ? res.data : res || [])
      },
      minChars: { type: Number, default: 1 },
      debounceMs: { type: Number, default: 300 },
      itemText: { type: String, default: 'text' },
      itemValue: { type: String, default: 'value' },
      label: { type: String, default: '' },
      placeholder: { type: String, default: '' },
      multiple: { type: Boolean, default: false },
      clearable: { type: Boolean, default: true },
      noDataText: { type: String, default: 'Sem resultados' },
      hideNoData: { type: Boolean, default: false },
      dense: { type: Boolean, default: true },
      outlined: { type: Boolean, default: true },
      fetchOnMount: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false },
      filter: {
        type: Function,
        default() {
          return (itemText, queryText) => {
            const text = (itemText || '').toString().toLowerCase();
            const query = (queryText || '').toString().toLowerCase();
            return text.includes(query);
          };
        }
      }
    },
    emits: ['error', 'search', 'blur', 'change', 'select', 'results', 'input'],

    data() {
      return {
        items: [],
        loading: false,
        search: '',
        lastRequestId: 0,
        debounceTimer: null
      };
    },

    computed: {
      internalValue: {
        get() {
          return this.value;
        },
        set(val) {
          this.$emit('input', val); // v-model atualiza pai
        }
      }
    },

    created() {
      this.debouncedFetch = (q) => {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.performSearch(q), this.debounceMs);
      };
    },

    mounted() {
      if (this.fetchOnMount) this.performSearch('');
    },

    beforeUnmount() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
    },

    methods: {
      onSelect(item) {
        this.$emit('select', item);
      },

      onChange(item) {
        this.$emit('change', item);
      },

      onBlur() {
        this.$emit('blur');
      },

      onSearchInput(val) {
        this.search = val;
        this.$emit('search', val);
      },

      normalizeSelected(selected) {
        const arr = Array.isArray(selected) ? selected : [selected];
        return arr.map((val) => (typeof val === 'object' ? val : { [this.itemText]: val, [this.itemValue]: val }));
      },

      emitSelectedValue(value) {
        const arr = Array.isArray(value) ? value : [value];
        const mapped = arr.map((item) =>
          item && typeof item === 'object' && this.itemValue in item ? item[this.itemValue] : item
        );
        return this.multiple ? [...new Set(mapped)] : mapped[0];
      },

      async performSearch(query) {
        if ((query == null || query.length < this.minChars) && !(this.fetchOnMount && query === '')) return;

        if (!this.service || typeof this.service[this.serviceMethod] !== 'function') {
          console.error(`[AutocompleteAssincrono] Método '${this.serviceMethod}' não encontrado.`);
          this.items = [];
          return;
        }

        const requestId = ++this.lastRequestId;
        const campo = this.$refs.autocomplete;
        const input = campo && campo.$el ? campo.$el.querySelector('input') : null;
        const tinhaFoco = input && document.activeElement === input;
        const consulta = query;
        this.loading = true;

        let params = {};

        if (this.paramsTransform) {
          try {
            params = this.paramsTransform(query, this.extraParams);
          } catch (err) {
            params = { [this.queryParamName]: query, ...this.extraParams };
          }
        } else {
          params = { [this.queryParamName]: query, ...this.extraParams };
        }

        try {
          const response = await this.service[this.serviceMethod](params);
          if (requestId !== this.lastRequestId) return;

          const fetchedItems = Array.isArray(this.responseParser(response)) ? this.responseParser(response) : [];

          const selectAllItem =
            this.multiple && fetchedItems.length > 0
              ? [
                  {
                    [this.itemText]: 'SELECIONAR TUDO',
                    [this.itemValue]: 'SELECIONAR TUDO'
                  }
                ]
              : [];

          this.items = Array.from(new Set([...selectAllItem, ...fetchedItems]));

          this.$nextTick(() => {
            if (consulta && this.search !== consulta) this.search = consulta;
            if (tinhaFoco && input && document.activeElement !== input) input.focus();
          });

          this.$emit('results', this.emitSelectedValue(this.items), query);
        } catch (err) {
          if (requestId !== this.lastRequestId) return;
          console.error('[AutocompleteAssincrono] Erro:', err);
          this.$emit('error', err);
          this.items = [];
        } finally {
          if (requestId === this.lastRequestId) this.loading = false;
        }
      },

      clear() {
        // Limpa o autocomplete manualmente
        this.items = [];
        this.internalValue = this.multiple ? [] : null;
        this.search = '';
      }
    },

    watch: {
      search(newQuery) {
        this.debouncedFetch(newQuery);
      }
    }
  };
</script>
